import { action } from '@ember/object';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  startOfDay,
  endOfDay,
  getUnixTime,
  addYears,
  subMinutes,
  startOfMonth,
  addMonths,
  subMonths,
  isToday,
  isYesterday,
  isTomorrow,
} from 'date-fns';
import { format } from 'date-fns-tz';
import type WorkplaceDayModel from 'garaje/models/workplace-day';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import { endTimeOptions, getPartialTimes, hourOptions, startTimeOptions } from 'garaje/utils/hour-options';

interface Option {
  value: string;
  label: string;
}

interface MapDateSelectArgs {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  onAllDayToggle: () => void;
  allDayEnabled: boolean;
  selectedTime: { startTime: string; endTime: string };
  onPartialTimeChange?: (key: 'startTime' | 'endTime', time: string) => void;
  workplaceDays: WorkplaceDayModel[];
}

export default class MapDateSelect extends Component<MapDateSelectArgs> {
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare featureFlags: FeatureFlagsService;

  @tracked showCalendar = false;
  @tracked showingDate = new Date();

  @action
  handleFocusOut(event: FocusEvent): void {
    const root = event.currentTarget as HTMLElement;
    const next = (event.relatedTarget as Node) || null;

    if (!next || !root.contains(next)) {
      this.showCalendar = false;
    }
  }

  @action
  goBackOneMonth(): void {
    this.showingDate = subMonths(this.showingDate, 1);
  }

  @action
  goToNextMonth(): void {
    this.showingDate = addMonths(this.showingDate, 1);
  }

  @action
  onDateSelect(date: Date): void {
    const { onDateSelect } = this.args;
    onDateSelect(date);
  }

  @action
  onPartialTimeChange(key: 'startTime' | 'endTime', option: Option): void {
    const { onPartialTimeChange } = this.args;
    if (onPartialTimeChange) {
      onPartialTimeChange(key, option.value);
    }
  }

  get formattedDate(): string {
    const { selectedDate } = this.args;
    const displayDate = subMinutes(selectedDate, this.state.minutesBetweenTimezones(selectedDate));

    if (isToday(displayDate)) {
      return 'Today';
    }
    if (isYesterday(displayDate)) {
      return 'Yesterday';
    }
    if (isTomorrow(displayDate)) {
      return 'Tomorrow';
    }
    return format(displayDate, 'MM/dd/yyyy');
  }

  get currentMonth(): string {
    return format(startOfMonth(this.showingDate), 'yyyy-MM-dd');
  }

  get dateLimits(): { startDate: number; endDate: number } {
    const locationDate = this.state.getOfficeLocationTime(startOfDay(new Date()));

    return {
      startDate: getUnixTime(locationDate),
      endDate: getUnixTime(addYears(locationDate, 2)),
    };
  }

  // Business-hours helpers for the selected date
  get selectedDayOfWeek(): string {
    const { selectedDate } = this.args;
    return format(subMinutes(selectedDate, this.state.minutesBetweenTimezones(selectedDate)), 'EEEE');
  }

  get activeWorkplaceDay(): WorkplaceDayModel | undefined {
    return this.args.workplaceDays?.find((d) => d.active && d.dayOfWeek === this.selectedDayOfWeek);
  }

  get workplaceStartTime(): string {
    return this.activeWorkplaceDay?.startTime ?? '00:00:00';
  }

  get workplaceEndTime(): string {
    return this.activeWorkplaceDay?.endTime ?? '23:59:00';
  }

  get computedStartTime(): Date {
    const { selectedDate, allDayEnabled } = this.args;
    const locationDate = this.state.getOfficeLocationTime(startOfDay(selectedDate));

    if (allDayEnabled) {
      return locationDate;
    }

    const [partialStart] = getPartialTimes(locationDate, this.args.selectedTime);
    return partialStart!;
  }

  get computedEndTime(): Date {
    const { selectedDate, allDayEnabled } = this.args;
    const locationDate = this.state.getOfficeLocationTime(startOfDay(selectedDate));

    if (allDayEnabled) {
      return this.state.getOfficeLocationTime(endOfDay(selectedDate));
    }

    const [, partialEnd] = getPartialTimes(locationDate, this.args.selectedTime);
    return partialEnd!;
  }

  get computedStartTimeUnix(): number {
    return getUnixTime(this.computedStartTime);
  }

  get computedEndTimeUnix(): number {
    return getUnixTime(this.computedEndTime);
  }

  get startTimePlaceholder(): string | null {
    return null;
  }

  get endTimePlaceholder(): string | null {
    return this.workplaceEndTimeLabel;
  }

  get partialDayError(): string {
    return '';
  }

  get selectedForToday(): boolean {
    const { selectedDate } = this.args;
    return isToday(subMinutes(selectedDate, this.state.minutesBetweenTimezones(selectedDate)));
  }

  get nextAvailableStartTime(): string {
    if (this.selectedForToday) {
      const currentTime = format(new Date(), 'HH:mm:ss'); // no tz adjust to match tests
      const closestInterval = hourOptions(15, true).find(({ value }) => value.localeCompare(currentTime) >= 0);
      const next = closestInterval?.value || '23:45:00';
      // Respect workplace start time as a lower bound
      return next.localeCompare(this.workplaceStartTime) >= 0 ? next : this.workplaceStartTime;
    }
    return this.workplaceStartTime;
  }

  get startHourOptions(): Option[] {
    const { selectedTime } = this.args;
    const options = startTimeOptions(selectedTime.endTime, 15, true);

    // Only allow times >= nextAvailableStartTime and strictly before workplaceEndTime
    return options.filter((option) => {
      return (
        option.value.localeCompare(this.nextAvailableStartTime) >= 0 &&
        option.value.localeCompare(this.workplaceEndTime) < 0
      );
    });
  }

  get workplaceEndTimeLabel(): string {
    const [hh = 0, mm = 0, ss = 0] = (this.workplaceEndTime || '23:59:00').split(':').map((n) => parseInt(n, 10));
    const d = new Date();
    d.setHours(hh, mm, ss, 0);
    return format(d, 'h:mm aaa');
  }

  get endHourOptions(): Option[] {
    const { selectedTime } = this.args;
    const options = endTimeOptions(selectedTime.startTime, 15, true);

    // Only allow times <= workplaceEndTime and > workplaceStartTime
    return options.filter((option) => {
      return (
        option.value.localeCompare(this.workplaceStartTime) > 0 &&
        option.value.localeCompare(this.workplaceEndTime) <= 0
      );
    });
  }
}
